import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { brand } from "../mock";
import { Check, ArrowRight, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "lazote_exit_intent_v1";
const PAUSE_MS = 7 * 24 * 60 * 60 * 1000; // 7 Tage

const benefits = [
  "Unverbindlich & kostenlos",
  "Erste Einschätzung deiner Situation",
  "Klare Empfehlung für die nächsten Schritte",
];

const panelRows = [
  { label: "Active Directory", status: "gehärtet" },
  { label: "Microsoft 365", status: "abgesichert" },
  { label: "Berechtigungen", status: "minimiert" },
];

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Frequenz-Cap: nach Schliessen/Conversion 7 Tage Pause
    try {
      const last = localStorage.getItem(STORAGE_KEY);
      if (last && Date.now() - Number(last) < PAUSE_MS) return;
    } catch (e) {}

    let shown = false;
    let armTimer;
    let mobileTimer;
    let scrollHandler;
    let mouseHandler;

    const isTouch = window.matchMedia("(hover: none)").matches;

    const cleanup = () => {
      clearTimeout(armTimer);
      clearTimeout(mobileTimer);
      if (scrollHandler) window.removeEventListener("scroll", scrollHandler);
      if (mouseHandler) document.removeEventListener("mouseleave", mouseHandler);
    };

    const trigger = () => {
      if (shown) return;
      shown = true;
      setOpen(true);
      cleanup();
    };

    // Erst nach 3 s scharf schalten (verhindert versehentliches Auslösen)
    armTimer = setTimeout(() => {
      if (isTouch) {
        // Mobile: Zeit + Scrolltiefe (> 40 %)
        const scrollDepth = () => {
          const total = document.documentElement.scrollHeight - window.innerHeight;
          return total > 0 ? (window.scrollY) / total : 0;
        };
        mobileTimer = setTimeout(() => {
          if (scrollDepth() > 0.4) {
            trigger();
          } else {
            scrollHandler = () => {
              if (scrollDepth() > 0.4) trigger();
            };
            window.addEventListener("scroll", scrollHandler, { passive: true });
          }
        }, 12000);
      } else {
        // Desktop: Maus verlässt das Fenster nach oben
        mouseHandler = (e) => {
          if (e.clientY <= 0) trigger();
        };
        document.addEventListener("mouseleave", mouseHandler);
      }
    }, 3000);

    return cleanup;
  }, []);

  const remember = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) {}
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) remember();
  };

  const onCta = () => {
    remember();
    setOpen(false);
    try {
      window.__setContactSubjectPreset?.("Kostenloses Erstgespräch");
    } catch (e) {}
    const el = document.getElementById("kontakt");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const accent = brand.colors.cyan;
  const dark = brand.colors.navy;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="exit-intent-popup"
        className="w-[94vw] max-w-3xl overflow-hidden border-slate-200 bg-white p-0 sm:rounded-2xl"
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Dark on-brand mini status panel */}
          <div className="relative hidden min-h-[340px] overflow-hidden md:block" style={{ background: "linear-gradient(160deg, #0A111F 0%, #002D72 160%)" }}>
            <div className="pointer-events-none absolute inset-0 hero-grid" aria-hidden />
            <div className="pointer-events-none absolute -top-16 -right-10 h-56 w-56 rounded-full" style={{ background: "radial-gradient(circle, rgba(40,216,255,0.28), rgba(40,216,255,0) 70%)" }} aria-hidden />
            <div className="relative flex h-full flex-col justify-center gap-3 p-8">
              <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                lazote://security-status
              </div>
              {panelRows.map((r) => (
                <div key={r.label} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 backdrop-blur">
                  <span className="text-sm font-medium text-slate-100">{r.label}</span>
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider" style={{ color: accent }}>
                    <Check size={11} strokeWidth={3} />
                    {r.status}
                  </span>
                </div>
              ))}
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Baseline: CIS / NIST
              </div>
            </div>
          </div>

          {/* Inhalt */}
          <div className="p-7 md:p-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-[#F4F6F8] px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-wider" style={{ color: dark }}>
              <ShieldCheck size={13} />
              Bevor du gehst
            </div>

            <DialogTitle className="mt-4 font-display text-2xl font-bold leading-tight text-[#0A111F]">
              Sichere dir dein kostenloses Erstgespräch
            </DialogTitle>
            <DialogDescription className="mt-2 text-slate-600">
              In 20 Minuten finden wir heraus, wo deine grössten Risiken liegen – unverbindlich und ohne Verkaufsdruck.
            </DialogDescription>

            <ul className="mt-5 space-y-2.5 text-sm text-slate-700">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full" style={{ backgroundColor: "rgba(40,216,255,0.18)" }}>
                    <Check size={11} strokeWidth={3} style={{ color: dark }} />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <Button
              data-testid="exit-intent-cta"
              onClick={onCta}
              className="mt-7 w-full rounded-full py-6 text-base font-semibold transition-shadow hover:shadow-[0_0_24px_rgba(40,216,255,0.45)]"
              style={{ backgroundColor: accent, color: "#063247" }}
            >
              Kostenloses Erstgespräch sichern
              <ArrowRight size={17} className="ml-2" />
            </Button>

            <button
              data-testid="exit-intent-dismiss"
              onClick={() => handleOpenChange(false)}
              className="mt-3 w-full text-center text-xs text-slate-400 transition-colors hover:text-slate-600"
            >
              Nein danke, vielleicht später
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
